'use client';

import type { ReactNode } from 'react';

import Link from 'next/link';
import { useParams } from 'next/navigation';

import { bioUrl } from '@ps/functions/bio/bioUrls';
import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { BackLink } from '@ps/ui/navigation/BackLink';
import { ArrowLeft, MessageCircle, Plus, Settings } from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { ChannelListEmptyState } from '../channel-list/ChannelListEmptyState';
import { ChannelListItem } from '../channel-list/ChannelListItem';
import { ChannelAvatar } from '../common/ChannelAvatar';

interface Props {
  /**
   * Optional content rendered ahead of the title — used by the route
   * shell to inject the mobile burger trigger without this pane having
   * to know about the dashboard nav.
   */
  headerLeadingSlot?: ReactNode;
}

/**
 * Channel list pane — used as the master slot in the master/detail
 * shell. Wires the channels-state subject from `CommunityContext`
 * into the list. The active-row highlight is derived from the URL
 * (`…/community/channels/[slug]`), keeping the pane stateless.
 */
export function ChannelListMasterPane({ headerLeadingSlot }: Props = {}) {
  const {
    channelsState$,
    requestCreateChannel,
    isOwner,
    canManage,
    creatorName,
    creatorInfo,
    bioContext,
  } = useCommunityContext();
  const [state] = useBehaviorSubject(channelsState$);
  const params = useParams<{ slug?: string }>();
  const activeSlug = params?.slug;
  // Creating a channel opens a web editor — hidden inside the CommentSold
  // native app. Mirrors legacy `CommunityChannelList` `isAppUser` gate.
  const isAppUser = useIsAppUser();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-4 pb-2 pt-[calc(1.25rem+var(--safe-area-inset-top))]">
        {/* Visitor nav: leave back to the creator's bio + show whose channels
            these are. Shown on the visitor bio surface only — `creatorInfo` is
            undefined on the dashboard (which has its own nav). NOT gated on
            ownership: an owner previewing their own bio still needs the way back,
            and gating on the lazy-resolving `isOwner` made the header flash in and
            out as auth settled. Mirrors the DM chat header. */}
        {creatorInfo ? (
          <div className="mb-4 flex items-center gap-3">
            <BackLink
              href={bioUrl(bioContext).home()}
              aria-label={`Back to ${creatorInfo.name}`}
              className="text-raise/80 hover:bg-raise/10 hover:text-content-1 -ml-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </BackLink>
            <ChannelAvatar
              src={creatorInfo.avatar}
              name={creatorName ?? creatorInfo.name}
              size={36}
            />
            <p className="text-content-1 min-w-0 flex-1 truncate text-base font-semibold">
              {creatorName ?? creatorInfo.name}
            </p>
          </div>
        ) : null}
        <div className="text-brand-text flex items-center gap-2 text-lg font-bold">
          {headerLeadingSlot}
          <MessageCircle className="h-6 w-6 shrink-0" aria-hidden />
          Community Channels
        </div>
        <div className="border-line-strong/80 [[data-theme=visitor]_&]:border-line mt-3 flex items-center justify-between border-b pb-2">
          <span className="text-brand-text border-brand border-b-2 pb-2 text-sm font-semibold">
            Channels
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-content-1 text-sm">
            {isOwner
              ? 'Your Channels'
              : creatorName
                ? `${creatorName}'s Channels`
                : 'Channels'}
          </span>
          {canManage && !isAppUser ? (
            <button
              type="button"
              onClick={requestCreateChannel}
              className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1 text-sm font-medium"
            >
              <Plus className="h-4 w-4" aria-hidden /> Create New
            </button>
          ) : isOwner && !isAppUser ? (
            // Owner previewing their own visitor bio: management is disabled on
            // this surface, so route them to the dashboard community manager
            // instead of the (unavailable) inline create/admin flow.
            <Link
              href="/-/community"
              className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1 text-sm font-medium"
            >
              <Settings className="h-4 w-4" aria-hidden /> Manage
            </Link>
          ) : null}
        </div>
      </div>

      {/* Clear the fixed mobile tab bar (~60px item + safe-area, `lg:hidden`)
          so the centered empty state sits in the visible region and the last
          channel row isn't hidden behind it. Desktop has no tab bar. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-[calc(3.75rem_+_env(safe-area-inset-bottom))] lg:pb-4">
        {state.error && state.channels.length === 0 ? (
          <p
            role="alert"
            className="bg-danger/10 m-3 rounded-md border border-red-500/30 px-4 py-3 text-sm text-red-200"
          >
            Couldn&rsquo;t load your channels.{' '}
            {state.error.message ?? 'Please try again.'}
          </p>
        ) : state.isLoading && state.channels.length === 0 ? (
          <ChannelListSkeleton />
        ) : state.channels.length === 0 ? (
          <ChannelListEmptyState />
        ) : (
          <ul className="flex flex-col gap-0.5">
            {state.channels.map(channel => (
              <li key={channel.id}>
                <ChannelListItem
                  channel={channel}
                  active={channel.slug === activeSlug}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ChannelListSkeleton() {
  return (
    <ul aria-hidden className="flex flex-col gap-0.5 px-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 rounded-md px-3 py-3">
          <span className="bg-surface-2 h-11 w-11 shrink-0 animate-pulse rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="bg-surface-2 h-3 w-32 animate-pulse rounded" />
            <span className="bg-surface-1 h-3 w-24 animate-pulse rounded" />
            <span className="bg-surface-1 h-3 w-48 animate-pulse rounded" />
          </div>
        </li>
      ))}
    </ul>
  );
}
